import type { ComponentType } from "react";

import { cn } from "../lib/utils.ts";

// Clickable prompt-chip primitives. Used for follow-ups after an assistant
// reply and for the empty-state starter list in the task pane; the marketing
// site uses them to render static-looking chips in mockups.

export function Suggestions({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return <div className={cn("flex flex-wrap gap-1", className)}>{children}</div>;
}

type SuggestionProps = {
  onClick?: () => void;
  disabled?: boolean;
  icon?: ComponentType<{ className?: string }>;
  children: React.ReactNode;
  variant?: "chip" | "row";
  className?: string;
  style?: React.CSSProperties;
};

export function Suggestion({
  onClick,
  disabled,
  icon: Icon,
  children,
  variant = "chip",
  className,
  style,
}: SuggestionProps) {
  const base =
    "flex items-center gap-2 text-[var(--color-foreground)] transition-all hover:-translate-y-px hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none";
  const chip =
    "rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-2.5 py-1 text-[11px] hover:bg-[var(--color-secondary)]";
  const row =
    "w-full rounded-md border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2 text-left text-xs hover:bg-[var(--color-secondary)]";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={style}
      className={cn(base, variant === "chip" ? chip : row, className)}
    >
      {Icon && (
        <Icon className="size-3.5 shrink-0 text-[var(--color-muted-foreground)]" />
      )}
      <span className="truncate">{children}</span>
    </button>
  );
}
